import { useQuery } from '@apollo/client/react';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormHelperText,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import debounce from 'lodash/debounce';
import React, { useMemo, useState } from 'react';
import { useInput } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import {
  AdminGetBlockTrainingComponentsDocument,
  AdminListExercisesDocument,
  BlockTrainingComponent,
  ExerciseUnit,
  ExerciseUnitsDocument,
  TrainingPlanTemplateDetailsFragment,
  TrainingPlanWeekInput,
  TrainingPlanWorkoutInput,
} from '../../graphql/types';

/** Templates always consist of this many identical weeks. */
export const WEEKS_PER_TEMPLATE = 4;

const EXERCISE_SEARCH_LIMIT = 50;

/** The minimal exercise shape the editor needs, satisfied both by search
 * results (AdminExerciseBasics) and by exercises loaded from an existing
 * template. */
interface PlanEditorExerciseOption {
  id: string;
  name: string;
  displayName?: string | null;
  defaultUnit?: { value: ExerciseUnit } | null;
  thumbnail?: string | null;
  video?: string | null;
}

interface PlanEditorExercise {
  editorKey: number;
  exercise: PlanEditorExerciseOption | null;
  count: number | '';
  unit: ExerciseUnit;
}

interface PlanEditorBlock {
  editorKey: number;
  trainingComponent: BlockTrainingComponent | '';
  /** Every set in a block is identical, so we only capture how many there are. */
  numberOfSets: number | '';
  exercises: PlanEditorExercise[];
}

export interface PlanEditorWorkout {
  editorKey: number;
  blocks: PlanEditorBlock[];
}

/** Stable React keys for editor rows, which have no natural identity. */
let editorKeySequence = 0;
const nextEditorKey = () => {
  editorKeySequence += 1;
  return editorKeySequence;
};

const MAX_UNITS = new Set<ExerciseUnit>([
  ExerciseUnit.RepsMax,
  ExerciseUnit.TimeMax,
]);
export const isMaxUnit = (unit: ExerciseUnit): boolean => MAX_UNITS.has(unit);

const emptyExercise = (): PlanEditorExercise => ({
  editorKey: nextEditorKey(),
  exercise: null,
  count: '',
  unit: ExerciseUnit.Reps,
});

const emptyBlock = (): PlanEditorBlock => ({
  editorKey: nextEditorKey(),
  trainingComponent: '',
  numberOfSets: 1,
  exercises: [emptyExercise()],
});

const emptyWorkout = (): PlanEditorWorkout => ({
  editorKey: nextEditorKey(),
  blocks: [emptyBlock()],
});

const replaceAt = <T,>(items: T[], index: number, item: T): T[] =>
  items.map((existing, i) => (i === index ? item : existing));

const removeAt = <T,>(items: T[], index: number): T[] =>
  items.filter((_, i) => i !== index);

const moveItem = <T,>(items: T[], from: number, to: number): T[] => {
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/** Up/down arrows used to reorder blocks and exercises. */
const MoveButtons: React.FC<{
  label: string;
  index: number;
  total: number;
  onMove: (from: number, to: number) => void;
}> = ({ label, index, total, onMove }) => (
  <>
    <IconButton
      aria-label={`Move ${label} up`}
      size="small"
      disabled={index === 0}
      onClick={() => onMove(index, index - 1)}
    >
      <ArrowUpwardIcon fontSize="inherit" />
    </IconButton>
    <IconButton
      aria-label={`Move ${label} down`}
      size="small"
      disabled={index === total - 1}
      onClick={() => onMove(index, index + 1)}
    >
      <ArrowDownwardIcon fontSize="inherit" />
    </IconButton>
  </>
);

const sortByOrder = <T extends { order: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => a.order - b.order);

/**
 * Converts a template's existing weeks into editor state. Only the first week
 * is loaded (weeks are identical by construction) and only the first set of
 * each block (sets are identical by construction).
 */
export const weeksToEditorWorkouts = (
  weeks: TrainingPlanTemplateDetailsFragment['weeks'],
): PlanEditorWorkout[] => {
  const firstWeek = sortByOrder(weeks.edges.map(({ node }) => node))[0];
  const workouts = sortByOrder(
    (firstWeek?.workouts.edges ?? []).map(({ node }) => node),
  );

  return workouts.map((workout) => ({
    editorKey: nextEditorKey(),
    blocks: sortByOrder(workout.blocks.edges.map(({ node }) => node)).map(
      (block) => {
        const sets = sortByOrder(block.sets.edges.map(({ node }) => node));
        const firstSetExercises = sortByOrder(
          (sets[0]?.trainingPlanExercises.edges ?? []).map(({ node }) => node),
        );
        return {
          editorKey: nextEditorKey(),
          trainingComponent: block.trainingComponent.value,
          numberOfSets: sets.length,
          exercises: firstSetExercises.map((trainingPlanExercise) => ({
            editorKey: nextEditorKey(),
            exercise: trainingPlanExercise.exercise,
            count: trainingPlanExercise.count,
            unit: trainingPlanExercise.unit.value,
          })),
        };
      },
    ),
  }));
};

const validateWorkouts = (workouts?: PlanEditorWorkout[]) => {
  if (!workouts?.length) {
    return 'Add at least one workout';
  }
  for (const [workoutIndex, workout] of workouts.entries()) {
    const workoutLabel = `Workout ${workoutIndex + 1}`;
    if (!workout.blocks.length) {
      return `${workoutLabel} needs at least one block`;
    }
    for (const [blockIndex, block] of workout.blocks.entries()) {
      const blockLabel = `${workoutLabel}, block ${blockIndex + 1}`;
      if (!block.trainingComponent) {
        return `${blockLabel} needs a training component`;
      }
      if (Number(block.numberOfSets) < 1) {
        return `${blockLabel} needs at least one set`;
      }
      if (!block.exercises.length) {
        return `${blockLabel} needs at least one exercise`;
      }
      for (const [exerciseIndex, exercise] of block.exercises.entries()) {
        const exerciseLabel = `${blockLabel}, exercise ${exerciseIndex + 1}`;
        if (!exercise.exercise) {
          return `${exerciseLabel} needs a movement selected`;
        }
        if (!isMaxUnit(exercise.unit) && Number(exercise.count) < 1) {
          return `${exerciseLabel} needs a count of at least 1`;
        }
      }
    }
  }
  return undefined;
};

const buildWeeks = (workouts: PlanEditorWorkout[]): TrainingPlanWeekInput[] => {
  const workoutInputs: TrainingPlanWorkoutInput[] = workouts.map(
    (workout, workoutIndex) => ({
      order: workoutIndex,
      blocks: workout.blocks.map((block, blockIndex) => ({
        order: blockIndex,
        trainingComponent: block.trainingComponent as BlockTrainingComponent,
        sets: Array.from(
          { length: Number(block.numberOfSets) },
          (_, setIndex) => ({
            order: setIndex,
            trainingPlanExercises: block.exercises.map(
              (exercise, exerciseIndex) => ({
                order: exerciseIndex,
                exerciseId: exercise.exercise?.id,
                count: Number(exercise.count),
                unit: exercise.unit,
              }),
            ),
          }),
        ),
      })),
    }),
  );

  return Array.from({ length: WEEKS_PER_TEMPLATE }, (_, order) => ({
    order,
    workouts: workoutInputs,
  }));
};

/**
 * Form transform shared by the create and edit screens: expands the single
 * edited week into WEEKS_PER_TEMPLATE identical weeks.
 */
export const transformTrainingPlanTemplate = ({
  workouts,
  ...data
}: FieldValues) => ({
  ...data,
  weeks: buildWeeks(workouts as PlanEditorWorkout[]),
});

const EXERCISE_THUMBNAIL_WIDTH = 96;
const EXERCISE_VIDEO_PLAYBACK_RATE = 2;

const ExerciseOptionMedia: React.FC<{
  thumbnail?: string | null;
  video?: string | null;
  isActive: boolean;
}> = ({ thumbnail, video, isActive }) => {
  const setVideoPlaybackRate = (node: HTMLVideoElement | null) => {
    if (node) {
      node.playbackRate = EXERCISE_VIDEO_PLAYBACK_RATE;
    }
  };

  return (
    <Box
      sx={{
        width: EXERCISE_THUMBNAIL_WIDTH,
        aspectRatio: '16 / 9',
        flexShrink: 0,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'action.hover',
      }}
    >
      {isActive && video ? (
        <video
          ref={setVideoPlaybackRate}
          src={video}
          muted
          autoPlay
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          tabIndex={-1}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        thumbnail && (
          <img
            src={thumbnail}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )
      )}
    </Box>
  );
};

const ExerciseOptionRow: React.FC<{
  option: PlanEditorExerciseOption;
  optionProps: React.HTMLAttributes<HTMLLIElement>;
  isActive: boolean;
}> = ({ option, optionProps, isActive }) => (
  <Box
    component="li"
    {...optionProps}
    sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
  >
    <span style={{ flexGrow: 1 }}>{option.displayName || option.name}</span>
    <ExerciseOptionMedia
      thumbnail={option.thumbnail}
      video={option.video}
      isActive={isActive}
    />
  </Box>
);

const ExercisePicker: React.FC<{
  value: PlanEditorExerciseOption | null;
  onChange: (exercise: PlanEditorExerciseOption | null) => void;
}> = ({ value, onChange }) => {
  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const setSearchDebounced = useMemo(() => debounce(setSearch, 300), []);

  const { data, loading } = useQuery(AdminListExercisesDocument, {
    variables: {
      input: { first: EXERCISE_SEARCH_LIMIT },
      filter: { name: search || undefined, isArchived: false },
    },
  });

  React.useEffect(() => {
    setInputValue(value ? value.displayName || value.name : '');
  }, [value]);
  React.useEffect(
    () => () => setSearchDebounced.cancel(),
    [setSearchDebounced],
  );

  const options: PlanEditorExerciseOption[] = data?.data.nodes ?? [];

  return (
    <Autocomplete
      size="small"
      sx={{ flexGrow: 1, minWidth: 250 }}
      value={value}
      options={options}
      loading={loading}
      // Options are already filtered server-side by the name filter
      filterOptions={(x) => x}
      getOptionLabel={(option) => option.displayName || option.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onChange={(_, selected) => onChange(selected)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
        setSearchDebounced(newInputValue);
      }}
      onHighlightChange={(_, option) => setHighlightedId(option?.id ?? null)}
      renderOption={(optionProps, option) => {
        const { key, ...rest } = optionProps;
        return (
          <ExerciseOptionRow
            key={key}
            option={option}
            optionProps={rest}
            isActive={highlightedId === option.id}
          />
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label="Exercise" required />
      )}
    />
  );
};

const ExerciseRow: React.FC<{
  exercise: PlanEditorExercise;
  exerciseIndex: number;
  totalExercises: number;
  units: { name: string; value: ExerciseUnit }[];
  onChange: (exercise: PlanEditorExercise) => void;
  onRemove: () => void;
  onMove: (from: number, to: number) => void;
}> = ({
  exercise,
  exerciseIndex,
  totalExercises,
  units,
  onChange,
  onRemove,
  onMove,
}) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <ExercisePicker
      value={exercise.exercise}
      onChange={(selected) => {
        const unit = selected?.defaultUnit?.value ?? exercise.unit;
        onChange({
          ...exercise,
          exercise: selected,
          unit,
          count: isMaxUnit(unit) ? 0 : exercise.count,
        });
      }}
    />
    {isMaxUnit(exercise.unit) ? (
      <TextField
        size="small"
        label="Count"
        disabled
        sx={{ width: 100 }}
        value="max"
      />
    ) : (
      <TextField
        size="small"
        label="Count"
        type="number"
        required
        sx={{ width: 100 }}
        slotProps={{ htmlInput: { min: 1 } }}
        value={exercise.count}
        onChange={(event) =>
          onChange({
            ...exercise,
            count: event.target.value === '' ? '' : Number(event.target.value),
          })
        }
      />
    )}
    <TextField
      size="small"
      select
      label="Unit"
      sx={{ width: 180 }}
      value={exercise.unit}
      onChange={(event) => {
        const unit = event.target.value as ExerciseUnit;
        onChange({
          ...exercise,
          unit,
          count: isMaxUnit(unit) ? 0 : exercise.count,
        });
      }}
    >
      {units.map((unit) => (
        <MenuItem key={unit.value} value={unit.value}>
          {unit.name}
        </MenuItem>
      ))}
    </TextField>
    <MoveButtons
      label="exercise"
      index={exerciseIndex}
      total={totalExercises}
      onMove={onMove}
    />
    <IconButton aria-label="Remove exercise" onClick={onRemove}>
      <DeleteOutlineIcon />
    </IconButton>
  </Stack>
);

const BlockEditor: React.FC<{
  block: PlanEditorBlock;
  blockIndex: number;
  totalBlocks: number;
  trainingComponents: { name: string; value: BlockTrainingComponent }[];
  units: { name: string; value: ExerciseUnit }[];
  onChange: (block: PlanEditorBlock) => void;
  onRemove: () => void;
  onMove: (from: number, to: number) => void;
}> = ({
  block,
  blockIndex,
  totalBlocks,
  trainingComponents,
  units,
  onChange,
  onRemove,
  onMove,
}) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      padding: 2,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" marginBottom={2}>
      <Typography variant="subtitle2" sx={{ width: 60 }}>
        Block {blockIndex + 1}
      </Typography>
      <TextField
        size="small"
        select
        required
        label="Training Component"
        sx={{ width: 220 }}
        value={block.trainingComponent}
        onChange={(event) =>
          onChange({
            ...block,
            trainingComponent: event.target.value as BlockTrainingComponent,
          })
        }
      >
        {trainingComponents.map((component) => (
          <MenuItem key={component.value} value={component.value}>
            {component.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        label="Sets"
        type="number"
        required
        sx={{ width: 90 }}
        slotProps={{ htmlInput: { min: 1 } }}
        value={block.numberOfSets}
        onChange={(event) =>
          onChange({
            ...block,
            numberOfSets:
              event.target.value === '' ? '' : Number(event.target.value),
          })
        }
      />
      <Box flexGrow={1} />
      <MoveButtons
        label="block"
        index={blockIndex}
        total={totalBlocks}
        onMove={onMove}
      />
      <IconButton aria-label="Remove block" onClick={onRemove}>
        <DeleteOutlineIcon />
      </IconButton>
    </Stack>
    <Typography variant="caption" color="text.secondary">
      Exercises in each set
    </Typography>
    <Stack spacing={1} marginTop={1}>
      {block.exercises.map((exercise, exerciseIndex) => (
        <ExerciseRow
          key={exercise.editorKey}
          exercise={exercise}
          exerciseIndex={exerciseIndex}
          totalExercises={block.exercises.length}
          units={units}
          onChange={(updated) =>
            onChange({
              ...block,
              exercises: replaceAt(block.exercises, exerciseIndex, updated),
            })
          }
          onRemove={() =>
            onChange({
              ...block,
              exercises: removeAt(block.exercises, exerciseIndex),
            })
          }
          onMove={(from, to) =>
            onChange({
              ...block,
              exercises: moveItem(block.exercises, from, to),
            })
          }
        />
      ))}
      <Button
        startIcon={<AddIcon />}
        size="small"
        sx={{ alignSelf: 'flex-start' }}
        onClick={() =>
          onChange({
            ...block,
            exercises: [...block.exercises, emptyExercise()],
          })
        }
      >
        Add exercise
      </Button>
    </Stack>
  </Box>
);

/**
 * Editor for the structure of a single week. Every template is saved with
 * WEEKS_PER_TEMPLATE identical copies of this week.
 */
export const WeekStructureInput: React.FC<{
  source: string;
  defaultValue?: PlanEditorWorkout[];
}> = ({ source, defaultValue }) => {
  const { field, fieldState } = useInput({
    source,
    validate: validateWorkouts,
    defaultValue: defaultValue ?? [emptyWorkout()],
  });
  const workouts: PlanEditorWorkout[] = field.value ?? [];

  const trainingComponentsQuery = useQuery(
    AdminGetBlockTrainingComponentsDocument,
  );
  const trainingComponents =
    trainingComponentsQuery.data?.blockTrainingComponents ?? [];
  const unitsQuery = useQuery(ExerciseUnitsDocument);
  const units = unitsQuery.data?.exerciseUnits ?? [];

  return (
    <Box width="100%">
      <Typography variant="h6" gutterBottom>
        Weekly Structure
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Define one week of workouts. The plan is saved with {WEEKS_PER_TEMPLATE}{' '}
        identical weeks.
      </Typography>
      <Stack spacing={2}>
        {workouts.map((workout, workoutIndex) => (
          <Card key={workout.editorKey} variant="outlined">
            <CardHeader
              title={`Workout ${workoutIndex + 1}`}
              action={
                <>
                  <MoveButtons
                    label="workout"
                    index={workoutIndex}
                    total={workouts.length}
                    onMove={(from, to) =>
                      field.onChange(moveItem(workouts, from, to))
                    }
                  />
                  <IconButton
                    aria-label="Remove workout"
                    onClick={() =>
                      field.onChange(removeAt(workouts, workoutIndex))
                    }
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </>
              }
            />
            <CardContent>
              <Stack spacing={2}>
                {workout.blocks.map((block, blockIndex) => (
                  <BlockEditor
                    key={block.editorKey}
                    block={block}
                    blockIndex={blockIndex}
                    totalBlocks={workout.blocks.length}
                    trainingComponents={trainingComponents}
                    units={units}
                    onChange={(updated) =>
                      field.onChange(
                        replaceAt(workouts, workoutIndex, {
                          ...workout,
                          blocks: replaceAt(
                            workout.blocks,
                            blockIndex,
                            updated,
                          ),
                        }),
                      )
                    }
                    onRemove={() =>
                      field.onChange(
                        replaceAt(workouts, workoutIndex, {
                          ...workout,
                          blocks: removeAt(workout.blocks, blockIndex),
                        }),
                      )
                    }
                    onMove={(from, to) =>
                      field.onChange(
                        replaceAt(workouts, workoutIndex, {
                          ...workout,
                          blocks: moveItem(workout.blocks, from, to),
                        }),
                      )
                    }
                  />
                ))}
                <Button
                  startIcon={<AddIcon />}
                  size="small"
                  sx={{ alignSelf: 'flex-start' }}
                  onClick={() =>
                    field.onChange(
                      replaceAt(workouts, workoutIndex, {
                        ...workout,
                        blocks: [...workout.blocks, emptyBlock()],
                      }),
                    )
                  }
                >
                  Add block
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ))}
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => field.onChange([...workouts, emptyWorkout()])}
        >
          Add workout
        </Button>
        {fieldState.error?.message && (
          <FormHelperText error>{fieldState.error.message}</FormHelperText>
        )}
      </Stack>
    </Box>
  );
};
